import { Grid } from '@mui/material';
import { MembershipContext } from 'components/MembershipGuard';
import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CurrentMembership } from 'store/types/Membership';
import PaymentMethod from 'store/types/PaymentMethod';
import SubscriptionStatus from 'store/enums/SubscriptionStatus';
import MembershipService from 'services/api/MembershipService';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import EditPaymentMethodModal from 'components/payments/EditPaymentMethodModal';
import { PaymentMethodsContext } from 'components/PaymentMethodsContextWrapper';
import MembershipStatusCard from './MembershipStatusCard';
import { ParametersContext } from 'components/ParametersGuard';

const MembershipInfoPage: React.FunctionComponent = () => {
  const currentMembership: CurrentMembership = useContext(MembershipContext);
  const { enqueueSnackbar } = useSnackbar();
  const [addMethodModalOpen, setAddMethodModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const {
    membership: { membershipPageHeader },
  } = useContext(ParametersContext);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const { data, error, loading: paymentMethodsLoading } = useContext(PaymentMethodsContext);
  const { status, autoRenewal = false, setCurrentMembership, endDate = '', membershipLoading } = currentMembership;
  const isNonMember: boolean = status === SubscriptionStatus.NonMember;

  useEffect(() => {
    if (data && data.length) {
      setPaymentMethods(data);
    }
  }, [data]);

  useEffect(() => {
    setLoading(paymentMethodsLoading);
  }, [paymentMethodsLoading]);

  const changeAutoRenewal = useCallback(
    (isEnabled: boolean) => {
      setLoading(true);
      MembershipService.changeAutoRenewal(isEnabled)
        .then(() => {
          enqueueSnackbar(isEnabled ? 'Auto-Renewal successfully turned on' : 'Auto-Renewal successfully turned off', {
            variant: 'success',
          });
          setLoading(false);
          setCurrentMembership({ autoRenewal: isEnabled });
        })
        .catch((errorMessage: string) => {
          setLoading(false);
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [setCurrentMembership, enqueueSnackbar]
  );

  const handleAddMethodModalClose = useCallback(() => {
    setAddMethodModalOpen(false);
  }, []);

  const handleNewMethodAdded = useCallback(() => {
    setAddMethodModalOpen(false);
    changeAutoRenewal(true);
  }, [changeAutoRenewal]);

  const handleTurnOnClick = useCallback(() => {
    if (!paymentMethods.length) {
      setAddMethodModalOpen(true);
    } else {
      changeAutoRenewal(true);
    }
  }, [changeAutoRenewal, paymentMethods]);

  const handleTurnOffClick = useCallback(() => {
    changeAutoRenewal(false);
  }, [changeAutoRenewal]);

  return (
    <>
      <Grid {...defaultGridContainerProps}>
        {isNonMember && (
          <Grid {...defaultGridItemProps}>
            {membershipPageHeader && <p dangerouslySetInnerHTML={{ __html: membershipPageHeader }} />}
          </Grid>
        )}
        <Grid {...defaultGridItemProps}>
          <MembershipStatusCard />
        </Grid>
      </Grid>
      <EditPaymentMethodModal
        open={addMethodModalOpen}
        onSubmit={handleNewMethodAdded}
        onClose={handleAddMethodModalClose}
        makeDefaultPreselected={true}
      />
    </>
  );
};
export default MembershipInfoPage;
